import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmptyReportSection from './EmptyReportSection';
import { reportStyles } from './styles';

const formatReportValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

export default function FailureDetailsSection({ failureDetails = [] }) {
  const [selectedEventsByType, setSelectedEventsByType] = React.useState({});

  const handleEventFilterChange = (failureType, eventName) => {
    setSelectedEventsByType((prev) => {
      if (!eventName || prev[failureType] === eventName) {
        const next = { ...prev };
        delete next[failureType];
        return next;
      }

      return {
        ...prev,
        [failureType]: eventName,
      };
    });
  };

  if (failureDetails.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={reportStyles.reportPaperWithMargin}>
      <Box sx={{ mt: 1.5 }}>
        {failureDetails.map((details) => {
          const selectedEvent = selectedEventsByType[details.type];
          const visibleAttributes = selectedEvent
            ? details.attributes.filter((attribute) => attribute.event === selectedEvent)
            : details.attributes;

          return (
            <Accordion key={details.type} disableGutters variant="outlined" sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                    pr: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {details.type}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${details.count} failures`}
                      size="small"
                      color={details.count > 0 ? 'error' : 'success'}
                      variant="outlined"
                    />
                    <Chip
                      label={`${details.events.length} events`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${visibleAttributes.length} / ${details.attributes.length} attributes`}
                      size="small"
                      color={selectedEvent ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {details.events.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, mb: 1 }}>
                      Events
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label="All"
                        size="small"
                        color={!selectedEvent ? 'primary' : 'default'}
                        variant={!selectedEvent ? 'filled' : 'outlined'}
                        onClick={() => handleEventFilterChange(details.type)}
                      />
                      {details.events.map((eventName) => (
                        <Chip
                          key={`${details.type}-${eventName}`}
                          label={eventName}
                          size="small"
                          color={selectedEvent === eventName ? 'primary' : 'default'}
                          variant={selectedEvent === eventName ? 'filled' : 'outlined'}
                          onClick={() => handleEventFilterChange(details.type, eventName)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {details.attributes.length === 0 ? (
                  <EmptyReportSection>No attribute details returned.</EmptyReportSection>
                ) : visibleAttributes.length === 0 ? (
                  <EmptyReportSection>
                    No attribute details returned for this event.
                  </EmptyReportSection>
                ) : (
                  <TableContainer>
                    <Table size="small" aria-label={`${details.type} attributes`}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={reportStyles.tableHeaderCell}>Event</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>Attribute</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>Expected</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>mParticle</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>GMA</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>Source</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>Flow</TableCell>
                          <TableCell sx={reportStyles.tableHeaderCell}>Test Case</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleAttributes.map((attribute, index) => (
                          <TableRow
                            key={`${details.type}-${attribute.event || 'event'}-${
                              attribute.variable_name || 'attribute'
                            }-${index}`}
                          >
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.event)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.variable_name)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.expected_value)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.mparticle_actual)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.gma_actual)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.failing_source)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.flow_name || attribute.flow_id)}
                            </TableCell>
                            <TableCell sx={reportStyles.wordBreakCell}>
                              {formatReportValue(attribute.test_case)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
}
